import type { ReactNode } from 'react';
import Layout from '../../../components/layout/Layout';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import ArticleHero from '../../../components/news/ArticleHero';
import PullQuote from '../../../components/news/PullQuote';
import KeyPointsBox from '../../../components/news/KeyPointsBox';
import CommentBox from '../../../components/news/CommentBox';
import SourceCard from '../../../components/news/SourceCard';
import RelatedCards from '../../../components/news/RelatedCards';

/* ---- 記事内インライン部品（報道メディア型・帯は使わない） ---- */

function SectionHeading({ id, en, children }: { id: string; en: string; children: ReactNode }) {
  return (
    <div id={id} className="scroll-mt-28 mt-14 mb-6">
      <span aria-hidden="true" className="block w-10 h-px bg-[#a38e65] mb-4" />
      <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-2">{en}</span>
      <h2 className="jba-display font-medium text-[#1a2942] text-[24px] sm:text-[28px] leading-[1.4]">{children}</h2>
    </div>
  );
}

function SourceShot({ src, alt, media, url }: { src: string; alt: string; media: string; url: string }) {
  return (
    <figure className="my-9">
      <img src={src} alt={alt} loading="lazy" className="w-full h-auto border border-[#e2ddd2] shadow-[0_3px_20px_rgba(26,41,66,0.09)]" />
      <figcaption className="mt-2.5 text-[13px] text-[#8f7a56] font-['Noto_Serif_JP',serif] border-l-2 border-[#a38e65] pl-3 leading-[1.7]">
        出典: {media}（<a href={url} target="_blank" rel="noopener noreferrer" className="underline decoration-[#a38e65]/50 break-all hover:text-[#1a2942] transition-colors">{url}</a>）
      </figcaption>
    </figure>
  );
}

function Stat({ children }: { children: ReactNode }) {
  return <span className="font-['Playfair_Display',serif] text-[#8f7a56] text-[1.55em] font-bold tracking-tight px-[2px] align-[-0.06em]">{children}</span>;
}

const P = "text-[#2a2a2a] text-[17px] sm:text-[18px] leading-[1.9] font-['Noto_Serif_JP',serif]";

export default function RafflesButlerCampaignPage() {
  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: 'ニュース・お知らせ', href: '/news' },
    { label: 'Raffles「The Butler Did It」グローバルキャンペーン' }
  ];

  const article = {
    category: '業界動向',
    date: '2024年9月10日',
    title: 'Raffles Hotels & Resorts グローバルブランドキャンペーン「The Butler Did It」',
    subtitle: 'ラグジュアリーホテルの執事サービス革新',
    heroImage: '/images/generated/jba-fs-105-news_raffles-butler-campaign.webp',
    lead: '世界的ラグジュアリーホテルブランドのRaffles Hotels & Resortsが、伝説的なバトラーサービスを祝福する革新的なグローバルキャンペーン「The Butler Did It」を発表しました。ファッション業界の著名クリエイターを起用した本キャンペーンは、執事サービスの現代的価値を世界に発信する画期的な取り組みとして注目を集めています。',
    comment: 'Raffles Hotels & Resortsによる「The Butler Did It」キャンペーンは、執事サービスの真の価値を世界に発信する画期的な取り組みとして、弊協会としても深く敬意を表しております。このキャンペーンが示すように、執事サービスは単なる高級サービスではなく、ゲストの人生に真の価値をもたらす専門的な職業です。特に「ハイパーパーソナライゼーション」という概念は、私どもが日本で推進している「おもてなしの心」と深く共鳴するものであり、東西の文化を超えた執事サービスの普遍的価値を証明するものです。このような国際的な取り組みが、日本における執事文化の発展と社会的認知の向上に大きく寄与することを期待しており、私どもとしても世界水準の執事サービスの提供に向けて、より一層の努力を重ねてまいります。',
    related: [
      { to: '/news/raffles-london-campaign', image: '/images/generated/jba-fs-109-news_raffles-butler-campaign-v3.webp', aspect: 'aspect-[3/4]', alt: 'Raffles London「The Butler Did It」キャンペーン第2章の関連記事', title: 'Raffles London「The Butler Did It」キャンペーン第2章', desc: 'ロンドンを舞台にした続編キャンペーン' },
      { to: '/news/downton-abbey-grand-finale', image: '/images/generated/jba-fs-110-news_raffles-butler-campaign-v4.webp', aspect: 'aspect-[3/4]', alt: 'Downton Abbey: The Grand Finale 映画公開の関連記事', title: 'Downton Abbey: The Grand Finale 映画公開', desc: '執事文化の集大成として世界的注目' }
    ]
  };

  const sources = [
    { label: '出典元', value: 'Raffles Hotels & Resorts（Accor Pressroom 公式発表）', link: false },
    { label: 'キャンペーン発表', value: 'https://press.accor.com/raffles-the-butler-did-it', link: true },
    { label: '公式キャンペーンサイト', value: 'https://www.raffles.com/the-butler-did-it', link: true },
    { label: 'アクセス日', value: '2026年7月', link: false }
  ];

  const toc = [
    { id: 's-overview', label: 'キャンペーンの概要と背景' },
    { id: 's-talent', label: 'キャンペーンに起用された才能' },
    { id: 's-value', label: '執事サービスの現代的価値' },
    { id: 's-legacy', label: 'バトラーサービスの遺産' },
    { id: 's-impact', label: '業界への影響と反響' }
  ];

  return (
    <Layout>
      <ArticleHero eyebrow="News / Industry" category={article.category} title={article.title} subtitle={article.subtitle} date={article.date} image={article.heroImage} />
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-14">
          <div className="border-l-2 border-[#a38e65] pl-6 sm:pl-8">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">Summary</span>
            <p className="jba-display text-[#1a2942] text-lg sm:text-xl leading-[1.95] tracking-[0.01em]">{article.lead}</p>
          </div>
        </div>
      </section>

      <div className="bg-white">
        <div className="max-w-6xl mx-auto px-6 pt-12 pb-4 lg:grid lg:grid-cols-[minmax(0,1fr)_17rem] lg:gap-14">
          <article className="w-full max-w-[760px] mx-auto lg:mx-0">
            <KeyPointsBox
              title="この記事のポイント"
              points={[
                'キャンペーンは2024年9月10日に発表。Raffles Singaporeを舞台に、クリエイティブディレクション＝Trey Laird、写真＝Dylan Don、出演＝Waris Ahluwalia／Robert Rabensteiner／May Siu、バトラー役＝Tim Eastonが起用された',
                'Rafflesのバトラーサービスは1887年のシンガポール開業以来130年以上続く、ブランドの象徴的なシグネチャーサービスである',
                '本キャンペーンはプリント、デジタル動画、デジタルディスプレイ、有料ソーシャルを通じて世界同時に展開された'
              ]}
            />

            {/* 01: 概要と背景（既存本文・一字も削らず） */}
            <SectionHeading id="s-overview" en="Overview">キャンペーンの概要と背景</SectionHeading>
            <div className="space-y-6">
              <p className={P}>Raffles Hotels & Resortsは、130年以上にわたって培ってきた伝説的なバトラーサービスを現代的な視点で再解釈し、「The Butler Did It」という革新的なグローバルキャンペーンを展開しています。このキャンペーンは、単なるホテルマーケティングを超えて、執事サービスという文化的価値を世界に発信する重要な取り組みとして位置づけられています。</p>
              <p className={P}>キャンペーンの制作には、ファッション業界で著名なクリエイティブディレクターやフォトグラファーが参加し、執事サービスの美学と現代的な感性を融合させた独創的な表現を実現しています。</p>
            </div>
            <SourceShot
              src="/images/news-sources/raffles-butler-campaign-accor.jpg"
              alt="Accor Pressroomに掲載されたRaffles「The Butler Did It」キャンペーン発表プレスリリースのスクリーンショット。出演者と制作クレジットが記載されている"
              media="Accor Pressroom（Raffles Hotels & Resorts 公式発表）"
              url="https://press.accor.com/raffles-the-butler-did-it"
            />

            <div className="my-10 -mx-6 sm:mx-0">
              <PullQuote quote="130年以上にわたって培ってきた伝説的なバトラーサービスを、現代的な視点で再解釈する。" cite="Raffles「The Butler Did It」" />
            </div>

            {/* 増補: 起用された才能 */}
            <SectionHeading id="s-talent" en="Campaign Talent">キャンペーンに起用された才能</SectionHeading>
            <div className="space-y-6">
              <p className={P}>ファッション写真家Dylan Donが撮影し、Trey Lairdがクリエイティブディレクションを担当したビジュアルでは、デザイナー兼俳優のWaris AhluwaliaやファッションアイコンRobert RabensteinerらがRafflesのゲスト役を演じ、Tim Eastonがバトラーとして登場します。</p>
              <p className={P}>Rabensteinerによるスタイリングが加わり、Raffles Singaporeを背景に、ファッション性の高いイメージが展開されました。キャンペーンはプリント、デジタル動画、デジタルディスプレイ、有料ソーシャルの各チャネルで世界同時にローンチされています。</p>
            </div>

            {/* 02: 現代的価値（既存本文・一字も削らず） */}
            <SectionHeading id="s-value" en="Modern Value">執事サービスの現代的価値</SectionHeading>
            <div className="space-y-6">
              <p className={P}>キャンペーンでは、Rafflesの執事サービスが単なる高級サービスではなく、ゲスト一人ひとりの個性と好みを深く理解し、予期を超える体験を創造する「ハイパーパーソナライゼーション」の実現者として描かれています。</p>
              <p className={P}>現代の富裕層が求める真のラグジュアリーは、物質的な豪華さではなく、時間と体験の質であり、Rafflesの執事はその最高の体現者として位置づけられています。このような価値観の変化を的確に捉えた本キャンペーンは、業界内外から高い評価を受けています。</p>
            </div>

            {/* 増補: バトラーサービスの遺産 */}
            <SectionHeading id="s-legacy" en="Butler Legacy">バトラーサービスの遺産</SectionHeading>
            <div className="space-y-6">
              <p className={P}>Rafflesのバトラーサービスは<Stat>1887年</Stat>のシンガポール開業以来、ゲストのあらゆるニーズを先回りして満たす伝説的なサービスとして知られています。ブランドの象徴として<Stat>130年</Stat>以上受け継がれてきた歴史が、本キャンペーンの根底にあります。</p>
              <p className={P}>公式キャンペーンサイトでは、空港送迎や朝食、シャンパン、スパトリートメントなど、バトラー監修のスイート宿泊特典が紹介され、サービスの具体的な実践例が示されています。</p>
            </div>
            <SourceShot
              src="/images/news-sources/raffles-butler-campaign-raffles.jpg"
              alt="Raffles Hotels & Resorts公式サイトの「The Butler Did It」キャンペーンページのスクリーンショット。バトラー監修のスイート宿泊特典が掲載されている"
              media="Raffles Hotels & Resorts 公式サイト"
              url="https://www.raffles.com/the-butler-did-it"
            />

            {/* 03: 業界への影響（既存本文・一字も削らず） */}
            <SectionHeading id="s-impact" en="Industry Impact">業界への影響と反響</SectionHeading>
            <div className="space-y-6">
              <p className={P}>「The Butler Did It」キャンペーンは、ホスピタリティ業界全体に大きな影響を与えており、他の高級ホテルブランドも執事サービスの価値を再評価し、類似の取り組みを検討し始めています。</p>
              <p className={P}>特に注目すべきは、このキャンペーンが執事という職業の社会的地位向上に寄与していることです。世界各地の執事養成機関では、キャンペーンの影響で入学希望者が増加しており、執事という職業への関心が国際的に高まっています。</p>
            </div>
          </article>

          <aside className="hidden lg:block">
            <div className="sticky top-28 space-y-8">
              <nav aria-label="目次" className="border border-[#e2ddd2] bg-[#f8f6f0] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Contents</span>
                <ul className="space-y-2.5">
                  {toc.map((t) => (
                    <li key={t.id}>
                      <a href={`#${t.id}`} className="text-[14px] text-[#3a4658] font-['Noto_Serif_JP',serif] leading-[1.6] hover:text-[#8f7a56] transition-colors border-l-2 border-transparent hover:border-[#a38e65] pl-3 -ml-3 block">{t.label}</a>
                    </li>
                  ))}
                </ul>
              </nav>

              <div className="border border-[#e2ddd2] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Article Info</span>
                <dl className="space-y-3 text-[13.5px] font-['Noto_Serif_JP',serif]">
                  <div><dt className="text-[#8f7a56]">発表日</dt><dd className="text-[#333] mt-0.5">2024年9月10日</dd></div>
                  <div><dt className="text-[#8f7a56]">ブランド</dt><dd className="text-[#333] mt-0.5">Raffles Hotels & Resorts</dd></div>
                  <div><dt className="text-[#8f7a56]">舞台</dt><dd className="text-[#333] mt-0.5">Raffles Singapore</dd></div>
                  <div><dt className="text-[#8f7a56]">カテゴリ</dt><dd className="text-[#333] mt-0.5">{article.category}</dd></div>
                </dl>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-8 pb-14">
          <CommentBox eyebrow="From the Association" heading="全日本執事協会からのコメント" body={article.comment} />
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pb-14">
          <SourceCard heading="出典" rows={sources} />
        </div>
      </section>

      <RelatedCards heading="関連記事" items={article.related} readLabel="詳しく" backHref="/news" backLabel="ニュース・お知らせ一覧へ戻る" />
    </Layout>
  );
}
